import Head from "next/head";
import { useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import dynamic from "next/dynamic";
import {
  HandThumbUpIcon,
  CpuChipIcon,
  FlagIcon,
  BoltIcon,
  LinkIcon,
  UserGroupIcon,
  ChartPieIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { SITE_URL } from "@/lib/constants";

const NewHeader = dynamic(
  () => import("@/components/NewHeader").then((mod) => mod.default),
  { ssr: true }
);
const Footer = dynamic(
  () => import("@/components/homepages/Footer").then((mod) => mod.default),
  { ssr: true }
);
const CallToAction = dynamic(
  () =>
    import("@/components/homepages/CallToAction").then(
      (mod) => mod.CallToAction
    ),
  { ssr: true }
);
const ComparisonHero = dynamic(
  () => import("@/components/comparison/ComparisonHero"),
  { ssr: true }
);
const ComparisonIntro = dynamic(
  () => import("@/components/comparison/ComparisonIntro"),
  { ssr: true }
);
const ComparisonDifferences = dynamic(
  () => import("@/components/comparison/ComparisonDifferences"),
  { ssr: true }
);
const ComparisonTable = dynamic(
  () => import("@/components/comparison/ComparisonTable"),
  { ssr: true }
);
const ComparisonPricing = dynamic(
  () => import("@/components/comparison/ComparisonPricing"),
  { ssr: true }
);
const ComparisonTestimonial = dynamic(
  () => import("@/components/comparison/ComparisonTestimonial"),
  { ssr: true }
);
const ComparisonBenefits = dynamic(
  () => import("@/components/comparison/ComparisonBenefits"),
  { ssr: true }
);
const ComparisonFAQ = dynamic(
  () => import("@/components/comparison/ComparisonFAQ"),
  { ssr: true }
);

// ─── Data ──────────────────────────────────────────────

const introCards = [
  {
    label: "AI AUTOMATION",
    labelColor: "text-blue-primary-500",
    icon: <CpuChipIcon className="w-4 h-4" />,
    title: "Smart categorisation & reconciliation",
    description:
      "AI processes transactions in real-time, auto-categorises expenses, matches bank feeds, and flags anomalies 24/7.",
  },
  {
    label: "HUMAN VERIFICATION",
    labelColor: "text-semantic-negative-500",
    icon: <UserIcon className="w-4 h-4" />,
    title: "Expert accountant review",
    description:
      "Every transaction verified by qualified Australian CAs and CPAs before anything is finalised — ensuring ATO compliance.",
  },
  {
    label: "REAL-TIME VISIBILITY",
    labelColor: "text-blue-primary-500",
    icon: <ChartBarIcon className="w-4 h-4" />,
    title: "Live dashboards & alerts",
    description:
      "24/7 access to P&L, cash flow, and KPIs — no waiting for your bookkeeper's monthly update.",
  },
];

const coreDifferences = [
  {
    icon: <HandThumbUpIcon className="w-5 h-5" />,
    title: "Full Service vs DIY Software",
    description:
      "Ledgerowl provides AI-powered accounting with human verification included. Xero is a do-it-yourself platform — you'll need to hire a bookkeeper separately or do the work yourself.",
  },
  {
    icon: <CpuChipIcon className="w-5 h-5" />,
    title: "AI + Human Hybrid Model",
    description:
      "Ledgerowl's AI automates categorisation, reconciliation, and anomaly detection — then qualified accountants verify every number. Xero offers basic automation rules that still require manual oversight.",
  },
  {
    icon: <FlagIcon className="w-5 h-5" />,
    title: "Built for Australia",
    description:
      "Ledgerowl is purpose-built for ATO compliance, BAS lodgement, STP reporting, and Australian banking integrations. Xero serves all countries — not all features are Australia-optimised.",
  },
  {
    icon: <BoltIcon className="w-5 h-5" />,
    title: "Real-Time Financials",
    description:
      "Ledgerowl's financials update in real-time with AI-powered bank feeds and smart matching. Xero updates depend on your manual inputs or your bookkeeper — typically 15+ days after month-end.",
  },
  {
    icon: <LinkIcon className="w-5 h-5" />,
    title: "Native Integrations",
    description:
      "Ledgerowl has built-in connections to Xero, MYOB, CommBank, ANZ, Stripe, Shopify, and more — with AI handling the data flow. Xero requires manual setup for each integration.",
  },
];

const comparisonRows = [
  {
    feature: "Service model",
    leftStatus: "check",
    leftDetail: "Full accounting service + AI software",
    rightStatus: "partial",
    rightDetail: "Software only — requires separate bookkeeper or DIY",
  },
  {
    feature: "AI-powered automation",
    leftStatus: "check",
    leftDetail: "Auto-categorisation, smart reconciliation, anomaly detection",
    rightStatus: "partial",
    rightDetail: "Manual bank rules and suggested matches",
  },
  {
    feature: "Human accountant review",
    leftStatus: "check",
    leftDetail: "Every transaction reviewed before finalisation",
    rightStatus: "cross",
    rightDetail: "Must hire and manage separately",
  },
  {
    feature: "BAS preparation & lodgement",
    leftStatus: "check",
    leftDetail: "AI prepares, accountant reviews, lodged on time",
    rightStatus: "partial",
    rightDetail: "You or your bookkeeper must review and lodge",
  },
  {
    feature: "Real-time financial data",
    leftStatus: "check",
    leftDetail: "24/7 access to P&L, cash flow, KPIs",
    rightStatus: "partial",
    rightDetail: "Data only as current as last reconciliation",
  },
  {
    feature: "Australian bank integrations",
    leftStatus: "check",
    leftDetail: "CommBank, ANZ, Westpac, NAB + smart matching",
    rightStatus: "check",
    rightDetail: "Direct feeds from major AU banks",
  },
  {
    feature: "ATO compliance",
    leftStatus: "check",
    leftDetail: "AI monitors compliance, accountants ensure accuracy",
    rightStatus: "partial",
    rightDetail: "Compliance is your responsibility",
  },
  {
    feature: "Dedicated account manager",
    leftStatus: "check",
    leftDetail: "Named accountant you can call",
    rightStatus: "cross",
    rightDetail: "General support only",
  },
  {
    feature: "Setup & onboarding",
    leftStatus: "check",
    leftDetail: "We migrate your data and set everything up",
    rightStatus: "partial",
    rightDetail: "DIY setup with help docs",
  },
];

const benefits = [
  {
    icon: <CpuChipIcon className="w-6 h-6" />,
    title: "AI-Powered Books",
    description: "100% accuracy, available 24/7, powered by intelligent automation",
  },
  {
    icon: <UserGroupIcon className="w-6 h-6" />,
    title: "Dedicated Team",
    description: "Australian CAs & CPAs available 7 days a week",
  },
  {
    icon: <ChartPieIcon className="w-6 h-6" />,
    title: "Real-Time Dashboards",
    description: "KPIs, P&L, and cash flow in one live dashboard",
  },
  {
    icon: <DocumentTextIcon className="w-6 h-6" />,
    title: "BAS & Tax Ready",
    description: "AI-prepared, human-verified, lodged on time — every quarter",
  },
  {
    icon: <ShieldCheckIcon className="w-6 h-6" />,
    title: "ATO Compliant",
    description: "Built-in compliance monitoring with expert oversight",
  },
];

const faqItems = [
  {
    question: "Can I keep using Xero alongside Ledgerowl?",
    answer:
      "Absolutely. Ledgerowl integrates natively with Xero, so you can keep your existing Xero account while our AI and accountants manage your books through it. Many clients use Ledgerowl as the intelligence and service layer on top of their Xero data — getting the best of both worlds.",
  },
  {
    question:
      "What are the main differences between Ledgerowl and Xero for Australian businesses?",
    answer:
      "Ledgerowl is a full accounting service that combines AI automation with qualified Australian accountants. Your books are managed for you — from categorisation to BAS lodgement. Xero is accounting software that provides the tools, but you (or a separately hired bookkeeper) must do the work. Ledgerowl is purpose-built for ATO compliance and Australian banking, while Xero serves businesses globally across all industries.",
  },
  {
    question: "How does Ledgerowl's AI actually work?",
    answer:
      "Our AI engine processes your bank feeds in real-time — automatically categorising transactions, matching invoices, reconciling accounts, and detecting anomalies. It learns your business patterns over time, becoming more accurate. Crucially, every AI-processed transaction is then reviewed by a qualified accountant before being finalised — giving you AI speed with human accuracy.",
  },
  {
    question:
      "Which is more cost-effective: Ledgerowl or Xero + a bookkeeper?",
    answer:
      "While Xero's sticker price starts at just $54/month, you still need someone to actually do the accounting. Even a graduate accountant costs ~$5,400/month in Australia (salary + super), bringing the true cost to $5,454+/month minimum. Experienced bookkeepers and accountants cost far more. Ledgerowl's Core plan starts at $399/month and includes everything: AI automation, dedicated human accountants, BAS lodgement, and real-time dashboards — saving you over $5,000/month compared to the Xero + hire route.",
  },
  {
    question: "How long does it take to get started with Ledgerowl?",
    answer:
      "Most businesses are fully onboarded within 48 hours. Our team handles the migration — connecting your bank feeds, importing historical data, and configuring your chart of accounts. If you're coming from Xero, the integration is seamless since we connect directly to your existing Xero account.",
  },
  {
    question: "Do I get a dedicated accountant I can actually talk to?",
    answer:
      "Yes. Every Ledgerowl client is assigned a named, qualified Australian accountant who knows your business inside and out. You can call, email, or message them directly — no ticket queues or chatbots. Behind the scenes, our AI handles the heavy lifting so your accountant can focus on what matters: ensuring accuracy and providing strategic advice.",
  },
];

const integrationLogos = [
  { name: "Xero", logo: "/logos/xero.svg", width: 80, height: 32 },
  { name: "MYOB", logo: "/logos/myob.svg", width: 80, height: 32 },
  { name: "Shopify", logo: "/logos/shopify.svg", width: 100, height: 32 },
  { name: "Stripe", logo: "/logos/stripe.svg", width: 80, height: 32 },
  { name: "Square", logo: "/logos/square.svg", width: 90, height: 32 },
  { name: "CommBank", logo: "/logos/commbank.svg", width: 120, height: 32 },
  { name: "ANZ", logo: "/logos/anz.svg", width: 70, height: 32 },
  { name: "PayPal", logo: "/logos/paypal.svg", width: 100, height: 32 },
];

// ─── Page ──────────────────────────────────────────────

export default function VsXero() {
  // Scroll-triggered fade-in animations
  useEffect(() => {
    if (typeof window === "undefined") return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".fade-in:not(.visible)").forEach((el) => io.observe(el));

    const mo = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.classList?.contains("fade-in") && !node.classList.contains("visible")) {
            io.observe(node);
          }
          node.querySelectorAll?.(".fade-in:not(.visible)").forEach((el) => io.observe(el));
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, []);

  return (
    <>
      <Head>
        <title>Ledgerowl vs Xero | AI Bookkeeping Service Australia</title>
        <meta
          name="description"
          content="Compare Ledgerowl with Xero. AI-powered bookkeeping with expert accountants vs DIY software. Automated BAS lodgement & human-verified accuracy included."
        />
        <meta
          property="og:title"
          content="Ledgerowl vs Xero | AI Bookkeeping Service Australia"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/vs-xero`} />
        <meta property="og:image" content="https://ledgerowl.com.au/favicon.png" />
        <meta property="og:site_name" content="Ledgerowl" />
        <meta property="og:locale" content="en_AU" />
        <meta
          name="keywords"
          content="Ledgerowl vs Xero, AI bookkeeping Australia, Xero alternative Australia, automated BAS lodgement Australia, AI accounting service Australia, bookkeeping service with accountant Australia"
        />
        <link rel="canonical" href={`${SITE_URL}/vs-xero`} />
        <link rel="icon" href="/favicon.png" />
      </Head>

      <NewHeader />

      <ComparisonHero
        title={
          <>
            AI-powered <span className="text-[#FF6B6B]">full-service</span>{" "}
            accounting vs. DIY software
          </>
        }
        subtitle="Which platform is best for growing Australian businesses that want accuracy, ATO compliance, and real-time financial visibility?"
        leftBrand={{
          label: "LEDGEROWL",
          className: "border-blue-primary-500/30 bg-blue-primary-500/[0.08]",
        }}
        rightBrand={{
          label: (
            <span className="text-[#13B5EA] text-2xl font-black tracking-normal lowercase">
              xero
            </span>
          ),
          className: "border-[#13B5EA]/30 bg-[#13B5EA]/[0.06]",
        }}
      />

      <ComparisonIntro
        heading={
          <>
            What&apos;s the real difference? Xero is{" "}
            <span className="text-blue-primary-500">software</span>. Ledgerowl
            is a{" "}
            <span className="text-semantic-negative-500">full service</span>.
          </>
        }
        paragraphs={[
          'While Xero gives you a tool to do your own bookkeeping — or to hire a bookkeeper separately — <strong>Ledgerowl does it all for you</strong>. Our AI automates 90% of the work in seconds, then real Australian accountants verify every number before it reaches your books.',
          'Ledgerowl is an AI-powered accounting service purpose-built for Australian businesses, offering native integrations with Australian banks and platforms, real-time BAS tracking, and ATO-ready books with minimal manual input. Xero is a widely used, general-purpose accounting software that requires manual upkeep or a separate bookkeeper.',
          'While Xero is flexible and customisable, it wasn\'t built to run your books for you — resulting in extra work, month-end backlogs, and reliance on third-party tools. <strong>Ledgerowl combines AI automation with human expertise in one package</strong>, making it ideal for businesses who want accurate, real-time financials without the overhead.',
        ]}
        cards={introCards}
      />

      <ComparisonDifferences
        title="Core differences"
        subtitle="The fundamental ways Ledgerowl and Xero approach accounting for Australian businesses"
        cards={coreDifferences}
      />

      <ComparisonTable
        title="Why leading Australian businesses choose Ledgerowl"
        subtitle="A side-by-side comparison of what you get with each platform"
        leftHeader="Ledgerowl"
        rightHeader="Xero"
        rows={comparisonRows}
      />

      <ComparisonPricing
        title="Pricing"
        subtitle="Ledgerowl offers flexible, growth-aligned pricing that includes both AI-powered software and human accounting services. Xero is software-only — you'll need to budget for a bookkeeper on top."
        leftCard={{
          brand: "Ledgerowl",
          tagline: "AI Automation + Human Accountants",
          price: "$399",
          priceSuffix: "/month",
          priceNote: "Core plan — includes everything",
          description:
            "Pricing scales with your annual revenue. Core includes AI-powered bookkeeping, bank reconciliation, BAS preparation & lodgement, a dedicated accountant, real-time dashboards, and unlimited integrations. Premium plan with CFO advisory and custom reporting from $899/month.",
        }}
        rightCard={{
          brand: "Xero",
          brandColor: "#13B5EA",
          tagline: "Software only + you need to hire staff",
          price: "$54",
          priceSuffix: "/month",
          priceNote: "Standard plan — software only",
          hiddenCost: {
            label: "Hidden cost: You still need an accountant",
            amount: "~$5,400",
            suffix: "/month",
            detail:
              "Avg. cost of a graduate accountant in Australia ($58,500 salary + 11.5% super). That's before leave, training, office space, and software licenses.",
          },
          trueCost: {
            amount: "$5,454+",
            suffix: "/month",
            detail: "Xero + graduate accountant (minimum)",
          },
          description:
            "Xero's Standard plan ($54/month) or Premium ($81/month) covers the software only. Australian businesses then need to hire an accountant or bookkeeper — even a graduate starts at ~$5,400/month in employer costs. Experienced bookkeepers and accountants cost significantly more.",
        }}
      />

      <ComparisonTestimonial
        quote='We were paying for Xero plus a bookkeeper and still chasing month-end reports. <strong>Ledgerowl&apos;s AI handles our reconciliation instantly</strong> and our accountant Sarah actually knows our business. It&apos;s the best of both worlds.'
        authorInitials="MR"
        authorName="Marcus Reeves"
        authorRole="CEO, Outback Organics — Health & Wellness"
      />

      <ComparisonBenefits
        title="What you get with Ledgerowl"
        subtitle="AI speed with human trust — everything you need to keep your books flawless and your business growing"
        benefits={benefits}
      />

      {/* Integrations */}
      <section className="py-[100px] px-6 lg:px-10 bg-base-white">
        <div className="max-w-[1100px] mx-auto">
          <h2 className="font-inter text-[24px] sm:text-[32px] lg:text-[38px] font-black tracking-[-1.5px] leading-[1.15] mb-4 text-center fade-in">
            We integrate with the platforms you already use
          </h2>
          <div className="flex gap-4 flex-wrap justify-center mt-10 fade-in">
            {integrationLogos.map((item) => (
              <div
                key={item.name}
                className="bg-cream border border-gray-cool-200 rounded-xl py-4 px-7 flex items-center gap-2 transition-all duration-200 hover:border-blue-primary-500 hover:text-blue-primary-500 hover:-translate-y-0.5"
              >
                <Image
                  src={item.logo}
                  alt={item.name}
                  width={item.width}
                  height={item.height}
                  className="h-[24px] w-auto object-contain"
                />
              </div>
            ))}
          </div>
          <p className="text-center mt-5 fade-in">
            <Link
              href="/contact"
              className="text-blue-primary-500 text-sm font-semibold hover:underline inline-flex items-center gap-1"
            >
              See all integrations
              <svg
                className="w-3 h-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </Link>
          </p>
        </div>
      </section>

      <ComparisonFAQ
        title="Frequently asked questions"
        subtitle="Everything you need to know about switching from Xero to Ledgerowl"
        items={faqItems}
      />

      <CallToAction />

      <Footer />
    </>
  );
}
